/**
 * Extraction Operations API Module
 * Handles extraction scheduling, batch management, and kanban board operations
 */

import { type RequestFunction } from './types'
import type {
  ExtractionMachine,
  ExtractionMachineCreateInput,
  ScheduledStrain,
  ScheduledStrainCreateInput,
  ScheduledStrainUpdateInput,
  BulkMoveInput,
  BatchBulkScanInput,
  BatchMinimal,
  BulkScanResponse,
  ExtractionKanbanResponse,
  ExtractionDashboard,
  PaginatedScheduledStrains,
  PaginatedExtractionMachines,
  FilterParams,
} from './types'

export class ExtractionApi {
  constructor(private request: RequestFunction) {}

  // ============================================================================
  // EXTRACTION MACHINES MANAGEMENT
  // ============================================================================

  async getMachines(params?: FilterParams): Promise<PaginatedExtractionMachines> {
    return this.request('/api/extraction/machines/', { params })
  }

  async getMachine(id: number): Promise<ExtractionMachine> {
    return this.request(`/api/extraction/machines/${id}/`)
  }

  async createMachine(data: ExtractionMachineCreateInput): Promise<ExtractionMachine> {
    return this.request('/api/extraction/machines/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateMachine(id: number, data: Partial<ExtractionMachineCreateInput>): Promise<ExtractionMachine> {
    return this.request(`/api/extraction/machines/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteMachine(id: number): Promise<void> {
    return this.request(`/api/extraction/machines/${id}/`, {
      method: 'DELETE'
    })
  }

  // ============================================================================
  // SCHEDULED STRAINS MANAGEMENT
  // ============================================================================

  async getSchedules(params?: FilterParams & {
    date?: string;
    machine?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedScheduledStrains> {
    return this.request('/api/extraction/schedules/', { params })
  }

  async getSchedule(id: number): Promise<ScheduledStrain> {
    return this.request(`/api/extraction/schedules/${id}/`)
  }

  async createSchedule(data: ScheduledStrainCreateInput): Promise<ScheduledStrain> {
    return this.request('/api/extraction/schedules/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async updateSchedule(id: number, data: ScheduledStrainUpdateInput): Promise<ScheduledStrain> {
    return this.request(`/api/extraction/schedules/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async deleteSchedule(id: number): Promise<void> {
    return this.request(`/api/extraction/schedules/${id}/`, {
      method: 'DELETE'
    })
  }

  async bulkMoveSchedules(data: BulkMoveInput): Promise<{
    updated_count: number;
    schedules: ScheduledStrain[];
  }> {
    return this.request('/api/extraction/schedules/bulk_move/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // BATCH SCANNING
  // ============================================================================

  async bulkScan(data: BatchBulkScanInput): Promise<BulkScanResponse> {
    return this.request('/api/extraction/bulk-scan/', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ============================================================================
  // KANBAN BOARD
  // ============================================================================

  async getKanban(): Promise<ExtractionKanbanResponse> {
    return this.request('/api/extraction/kanban/')
  }

  async updateBatchStatus(batchId: number, status: string): Promise<BatchMinimal> {
    return this.request(`/api/extraction/batches/${batchId}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  // ============================================================================
  // DASHBOARD
  // ============================================================================

  async getDashboard(): Promise<ExtractionDashboard> {
    return this.request('/api/extraction/dashboard/')
  }
}
